import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { keys } from 'remeda';
import { z } from 'zod/v4';

import {
  type ApiResponse,
  addActionNameToMutationResult,
  handleZodError,
  useLegacyAxiosInstance,
} from './utils';

const CustomVariableValueSchema = z.union([
  z.boolean(),
  z.string(),
  z.number(),
  z.date(),
  z.null(),
]);

export const CustomVariablesSchema = z.object({
  custom_variables: z.record(z.string(), CustomVariableValueSchema),
});

const CustomVariablesTransformed = CustomVariablesSchema.transform(data => {
  const transformedData: Record<string, string | null> = {};

  keys(data.custom_variables).forEach(key => {
    const value = data.custom_variables[key];

    if (typeof value === 'boolean') {
      transformedData[key] = value ? 'true' : 'false';
    } else if (typeof value === 'number') {
      transformedData[key] = value.toString();
    } else if (value instanceof Date) {
      transformedData[key] = dayjs(value).format('L');
    } else {
      transformedData[key] = value;
    }
  });

  return { custom_variables: transformedData };
});

export type UpdateClientCustomVariables = z.infer<typeof CustomVariablesSchema>;

export async function updateClientCustomVariablesFn(
  axiosInstance: Promise<AxiosInstance>,
  { id, data }: { id: string; data: UpdateClientCustomVariables },
) {
  const instance = await axiosInstance;
  const safeParse = CustomVariablesTransformed.safeParse(data);
  if (!safeParse.success) {
    handleZodError(safeParse, 'client custom variables');
  }

  return instance.put(`/debtors/${id}`, safeParse.data);
}

export function useUpdateClientCustomVariables() {
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientCustomVariables }
  >({
    mutationFn: variables => updateClientCustomVariablesFn(axiosInstance, variables),
  });

  return addActionNameToMutationResult<
    ApiResponse,
    AxiosError<{ error_message: Array<string> }>,
    { id: string; data: UpdateClientCustomVariables },
    'updateClientCustomVariables'
  >('updateClientCustomVariables', mutation);
}
